import React, { useEffect, useRef, useState } from 'react';
import ReactSelect, {
  ActionMeta,
  MultiValueGenericProps,
  MultiValue as MultiValueType,
  components
} from 'react-select';

import { customStyles } from './select-styles';
import Label from '../label';
import useWindowSize from '@hooks/useWindowSize';

export type SelectValue = {
  value: string | number;
  label: string | number;
};

type MultiSelectValue = {
  value: string | number;
  label: string | number;
};

type SelectBoxType = {
  name: string;
  label?: string;
  id?: string;
  placeholder?: string;
  onChange: (option: any) => void;
  options: any;
  value: SelectValue | null;
  disabled?: boolean;
  menuPlacement?: 'bottom' | 'top' | 'auto';
  dataCy?: string;
  isDisabled?: boolean;
  className?: string;
  maxMenuHeight?: number;
};

const Select = ({
  name,
  label,
  id,
  placeholder,
  onChange,
  options = [],
  value = { value: '', label: '' },
  menuPlacement = 'auto',
  dataCy,
  isDisabled,
  className,
  maxMenuHeight,
  ...rest
}: SelectBoxType) => {
  const [menuPortalTarget, setMenuPortalTarget] = useState<HTMLElement | null>(
    null
  );

  useEffect(() => {
    setMenuPortalTarget(document.getElementById('root'));
  }, []);

  return (
    <div className="relative" id={id}>
      {label && <Label htmlFor={name}>{label}</Label>}
      <div data-cy={dataCy} className="mt-2">
        <ReactSelect
          className={`scrollbar-hide ${className}`}
          classNamePrefix="ffh-select"
          name={name}
          isMulti={false}
          styles={customStyles}
          value={value}
          onChange={(option: SelectValue) => {
            onChange(option);
          }}
          options={options}
          isSearchable={false}
          menuPlacement={menuPlacement}
          maxMenuHeight={maxMenuHeight ?? 240}
          menuPortalTarget={menuPortalTarget}
          isDisabled={isDisabled}
          placeholder={placeholder}
          {...rest}
        />
      </div>
    </div>
  );
};

export default Select;

export type MultiSelectProps = {
  options: any[];
  value: MultiSelectValue[];
  onChange: (arg0: any, arg1: any) => void;
  label?: string;
  placeholder?: string;
};

export const MultiSelect = (props: MultiSelectProps) => {
  const { onChange, options, value, label, placeholder } = props;

  const selectedVals = value.map((x) => x.value);
  const hiddenOptions = selectedVals.slice(0, selectedVals.length);
  const filtered = options.filter((x) => !hiddenOptions.includes(x.value));

  const valueRef = useRef(value);
  valueRef.current = value;

  const selectAllOption = {
    value: '<SELECT_ALL>',
    label: 'Select all'
  };

  const isSelectAllSelected = () => valueRef.current.length === options.length;

  const isOptionSelected = (option: { value: string | number }) =>
    valueRef.current.some(({ value }) => value === option.value) ||
    isSelectAllSelected();

  const getValue = () => (isSelectAllSelected() ? [selectAllOption] : value);

  const handleChange = (
    newValue: MultiValueType<Record<string, any>>,
    actionMeta: ActionMeta<Record<string, any>>
  ) => {
    const { action, option, removedValue } = actionMeta;

    if (action === 'select-option' && option?.value === selectAllOption.value) {
      onChange(options, actionMeta);
    } else if (
      (action === 'deselect-option' &&
        option?.value === selectAllOption.value) ||
      (action === 'remove-value' &&
        removedValue.value === selectAllOption.value)
    ) {
      onChange([], actionMeta);
    } else if (action === 'remove-value' && newValue.length === 0) {
      onChange(options, actionMeta);
    } else if (
      actionMeta.action === 'deselect-option' &&
      isSelectAllSelected()
    ) {
      onChange(
        options.filter(({ value }) => value !== option?.value),
        actionMeta
      );
    } else if (action === 'clear') {
      onChange(options, actionMeta);
    } else {
      onChange(newValue || [], actionMeta);
    }
  };

  return (
    <div className="relative">
      {label && <Label>{label}</Label>}
      <ReactSelect
        classNamePrefix="ffh-select"
        isOptionSelected={isOptionSelected}
        options={[selectAllOption, ...filtered]}
        value={getValue()}
        onChange={handleChange}
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        isMulti
        isSearchable={false}
        components={{
          MultiValue,
          MultiValueLabel
        }}
        closeMenuOnScroll
        menuPlacement="auto"
        styles={customStyles}
        placeholder={placeholder}
      />
    </div>
  );
};

const MoreSelectedBadge = ({ items }) => {
  const style = {
    marginLeft: 'auto',
    background: '#d4eefa',
    borderRadius: '4px',
    fontFamily: 'Poppins',
    fontSize: '11px',
    padding: '3px',
    order: 99
  };

  const title = items.join(', ');
  const length = items.length;
  const label = `+ ${length} item${length !== 1 ? 's' : ''}`;

  return (
    <div style={style} title={title}>
      {label}
    </div>
  );
};

const MultiValue = ({ index, getValue, ...props }) => {
  const { width } = useWindowSize();
  const maxToShow = width < 400 ? 2 : 3;

  const overflow = getValue()
    .slice(maxToShow)
    .map((x) => x.label);

  return index < maxToShow ? (
    // @ts-ignore
    <components.MultiValue {...props} />
  ) : index === maxToShow ? (
    <MoreSelectedBadge items={overflow} />
  ) : null;
};

const MultiValueLabel = (props: MultiValueGenericProps) => {
  const { data } = props;

  return (
    <>
      <components.MultiValueLabel {...props}>
        <span className="font-open-sans text-base text-neutral-800">{`${
          !data.short ? data.label : data.short
        }`}</span>
      </components.MultiValueLabel>
    </>
  );
};
